import { useEffect, useState, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, RefreshCw, Lock, Inbox, MessageCircle, Mail, Download, KeyRound, X,
  ChevronDown, Sparkles, Copy, FileText,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { API, waLink } from "@/lib/site";

const STATUS_OPTIONS = ["New", "Contacted", "Won"];

const STATUS_CLS = {
  New: "border-[#FF6B00]/50 bg-[#FF6B00]/10 text-[#FF6B00]",
  Contacted: "border-sky-400/50 bg-sky-400/10 text-sky-300",
  Won: "border-[#10B981]/50 bg-[#10B981]/10 text-[#10B981]",
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-ZA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const fmtSize = (bytes) =>
  bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)}MB` : `${Math.max(1, Math.round(bytes / 1024))}KB`;

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [expandedId, setExpandedId] = useState(null);
  const [triagingId, setTriagingId] = useState(null);

  const [showPwPanel, setShowPwPanel] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwSubmitting, setPwSubmitting] = useState(false);

  const loadEnquiries = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/enquiries?limit=200`, { withCredentials: true });
      setEnquiries(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          const { data } = await axios.get(`${API}/enquiries?limit=200`, { withCredentials: true });
          setEnquiries(data);
        } catch {
          setUser(null);
          toast.error("Session expired — please sign in again.");
        }
      } else {
        toast.error("Could not load enquiries.", {
          description: formatApiErrorDetail(err?.response?.data?.detail),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (!cancelled) setUser(data);
      } catch {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
          if (!cancelled) setUser(data);
        } catch {
          if (!cancelled) setUser(null);
        }
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (user) loadEnquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      setPassword("");
    } catch (err) {
      toast.error("Sign-in failed.", {
        description: formatApiErrorDetail(err?.response?.data?.detail),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(null);
      setEnquiries([]);
    }
  };

  const setStatus = async (enquiryId, status) => {
    const prev = enquiries;
    setEnquiries((list) => list.map((q) => (q.id === enquiryId ? { ...q, status } : q)));
    try {
      const { data } = await axios.patch(`${API}/enquiries/${enquiryId}/status`, { status }, { withCredentials: true });
      setEnquiries((list) => list.map((q) => (q.id === enquiryId ? data : q)));
      toast.success(`Lead marked ${status}.`);
    } catch (err) {
      setEnquiries(prev);
      toast.error("Could not update status.", {
        description: formatApiErrorDetail(err?.response?.data?.detail),
      });
    }
  };

  const runTriage = async (enquiryId) => {
    setTriagingId(enquiryId);
    try {
      const { data } = await axios.post(`${API}/enquiries/${enquiryId}/triage`, {}, { withCredentials: true });
      setEnquiries((list) => list.map((q) => (q.id === enquiryId ? data : q)));
    } catch (err) {
      toast.error("AI triage is unavailable right now.", {
        description: formatApiErrorDetail(err?.response?.data?.detail),
      });
    } finally {
      setTriagingId(null);
    }
  };

  const copyReply = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Suggested reply copied to clipboard.");
    } catch {
      toast.error("Could not copy — please select the text manually.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/enquiries/export`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ngconde-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Enquiry spreadsheet downloaded.");
    } catch (err) {
      toast.error("Could not export enquiries. Please try again.");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    setPwSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, { currentPassword, newPassword }, { withCredentials: true });
      toast.success("Password updated.", { description: "Use your new password next time you sign in." });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowPwPanel(false);
    } catch (err) {
      toast.error("Could not change password.", {
        description: formatApiErrorDetail(err?.response?.data?.detail),
      });
    } finally {
      setPwSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-white/15 bg-[#080C14] px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00]";
  const labelCls =
    "mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400";

  if (user === undefined) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#080C14]">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Checking session...</p>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center bg-[#080C14] px-5">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0D1424] p-8">
          <div className="flex justify-center">
            <Logo compact />
          </div>
          <h1 className="mt-8 flex items-center justify-center gap-2 text-center font-display text-2xl font-black uppercase tracking-tight text-white">
            <Lock className="h-5 w-5 text-[#FF6B00]" />
            Admin Sign-In
          </h1>
          <p className="mt-2 text-center text-sm text-slate-400">Restricted to NGCONDE authorised staff.</p>
          <form onSubmit={login} data-testid="admin-login-form" className="mt-8 space-y-4">
            <div>
              <label htmlFor="admin-email" className={labelCls}>Email</label>
              <input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" className={inputCls} placeholder="you@company.co.za" />
            </div>
            <div>
              <label htmlFor="admin-password" className={labelCls}>Password</label>
              <input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" className={inputCls} placeholder="••••••••••••" />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="w-full rounded-full bg-[#FF6B00] px-8 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#080C14] transition-all hover:bg-[#E05E00] active:scale-[0.98] disabled:opacity-60"
            >
              {submitting ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  const counts = { All: enquiries.length };
  for (const s of STATUS_OPTIONS) counts[s] = enquiries.filter((q) => q.status === s).length;
  const filtered = statusFilter === "All" ? enquiries : enquiries.filter((q) => q.status === statusFilter);

  return (
    <main className="min-h-screen bg-[#080C14] px-5 py-8 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Logo compact />
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShowPwPanel((v) => !v)}
              data-testid="admin-password-toggle"
              className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:border-[#FF6B00] hover:text-[#FF6B00]"
            >
              <KeyRound className="h-3.5 w-3.5" /> Password
            </button>
            <button
              onClick={exportCsv}
              data-testid="admin-export-btn"
              className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:border-[#FF6B00] hover:text-[#FF6B00]"
            >
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
            <button
              onClick={loadEnquiries}
              data-testid="admin-refresh-btn"
              className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:border-[#FF6B00] hover:text-[#FF6B00]"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:border-red-500 hover:text-red-400"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>

        {showPwPanel && (
          <div data-testid="admin-password-panel" className="mt-6 rounded-2xl border border-white/10 bg-[#0D1424] p-6 md:p-8">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-display text-lg font-extrabold uppercase tracking-tight text-white">
                <KeyRound className="h-4 w-4 text-[#FF6B00]" /> Change Admin Password
              </h2>
              <button onClick={() => setShowPwPanel(false)} data-testid="admin-password-close" aria-label="Close password panel" className="rounded-md p-1 text-slate-400 transition-colors hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <form onSubmit={changePassword} data-testid="admin-password-form" className="mt-6 grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label htmlFor="pw-current" className={labelCls}>Current Password</label>
                <input id="pw-current" type="password" required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} data-testid="admin-password-current" className={inputCls} />
              </div>
              <div>
                <label htmlFor="pw-new" className={labelCls}>New Password (min 8)</label>
                <input id="pw-new" type="password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="admin-password-new" className={inputCls} />
              </div>
              <div>
                <label htmlFor="pw-confirm" className={labelCls}>Confirm New Password</label>
                <input id="pw-confirm" type="password" required minLength={8} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="admin-password-confirm" className={inputCls} />
              </div>
              <div className="sm:col-span-3">
                <button
                  type="submit"
                  disabled={pwSubmitting}
                  data-testid="admin-change-password-submit"
                  className="rounded-full bg-[#FF6B00] px-8 py-3 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#080C14] transition-all hover:bg-[#E05E00] active:scale-[0.98] disabled:opacity-60"
                >
                  {pwSubmitting ? "Updating..." : "Update Password"}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Inbox className="h-6 w-6 text-[#FF6B00]" />
          <h1 className="font-display text-2xl font-black uppercase tracking-tight text-white md:text-3xl">
            Partnership Enquiries
          </h1>
          {["All", ...STATUS_OPTIONS].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`admin-status-filter-${s.toLowerCase()}`}
              className={`rounded-full border px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] transition-all ${
                statusFilter === s
                  ? s === "All"
                    ? "border-white bg-white text-[#080C14]"
                    : STATUS_CLS[s]
                  : "border-white/15 text-slate-400 hover:border-white/40 hover:text-white"
              }`}
            >
              {s} · {counts[s]}
            </button>
          ))}
        </div>

        {filtered.length === 0 && !loading ? (
          <p className="mt-10 rounded-2xl border border-white/10 bg-[#0D1424] p-8 text-center text-sm text-slate-400">
            No enquiries in this view — new partnership submissions will appear here instantly.
          </p>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10">
            <table className="w-full min-w-[1180px] border-collapse text-left" data-testid="admin-enquiries-table">
              <thead>
                <tr className="border-b border-white/10 bg-[#0D1424]">
                  {["", "Received", "Name", "Company", "Contact", "Requirement", "Status", "Scope"].map((h, i) => (
                    <th key={i} className="px-4 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((q) => (
                  <Fragment key={q.id}>
                    <tr
                      data-testid="admin-enquiry-row"
                      className="border-b border-white/5 align-top transition-colors hover:bg-white/[0.03]"
                    >
                      <td className="px-2 py-4">
                        <button
                          onClick={() => setExpandedId(expandedId === q.id ? null : q.id)}
                          data-testid="admin-row-expand"
                          aria-label="Toggle enquiry details"
                          className="rounded-md p-1 text-slate-400 transition-colors hover:text-[#FF6B00]"
                        >
                          <ChevronDown className={`h-4 w-4 transition-transform ${expandedId === q.id ? "rotate-180" : ""}`} />
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 font-mono text-xs text-slate-400">{fmtDate(q.created_at)}</td>
                      <td className="px-4 py-4 text-sm font-semibold text-white">{q.full_name}</td>
                      <td className="px-4 py-4 text-sm text-slate-300">
                        {q.company_name}
                        {q.province && (
                          <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500">{q.province}</span>
                        )}
                        {q.urgency === "Urgent" && (
                          <span data-testid="admin-urgent-badge" className="mt-1.5 inline-block rounded-full border border-[#FF6B00]/60 bg-[#FF6B00]/15 px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-[#FF6B00]">
                            Urgent
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-300">
                        <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 break-all text-slate-300 transition-colors hover:text-[#FF6B00]">
                          <Mail className="h-3.5 w-3.5 shrink-0" /> {q.email}
                        </a>
                        <a
                          href={waLink(`Hello ${q.full_name}, thanks for your partnership enquiry to NGCONDE Courier Service.`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 flex items-center gap-1.5 text-[#25D366]"
                        >
                          <MessageCircle className="h-3.5 w-3.5 shrink-0" /> {q.phone}
                        </a>
                      </td>
                      <td className="px-4 py-4 text-xs text-[#FF6B00]">{q.service_type}</td>
                      <td className="px-4 py-4">
                        <select
                          value={q.status}
                          onChange={(e) => setStatus(q.id, e.target.value)}
                          data-testid="admin-status-select"
                          className={`appearance-none rounded-full border px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] outline-none transition-colors ${
                            STATUS_CLS[q.status] || STATUS_CLS.New
                          }`}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s} className="bg-[#0D1424] text-white">{s}</option>
                          ))}
                        </select>
                      </td>
                      <td className="max-w-sm px-4 py-4 text-sm leading-relaxed text-slate-300">{q.message}</td>
                    </tr>
                    {expandedId === q.id && (
                      <tr key={`${q.id}-detail`} data-testid="admin-ai-panel">
                        <td colSpan={8} className="border-b border-white/10 bg-[#0D1424]/60 px-5 py-6">
                          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                            <div>
                              <p className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-[#FF6B00]">
                                <Sparkles className="h-3.5 w-3.5" /> AI Summary
                              </p>
                              {q.ai_summary ? (
                                <p data-testid="admin-ai-summary" className="mt-2 text-sm leading-relaxed text-slate-200">{q.ai_summary}</p>
                              ) : (
                                <p className="mt-2 text-sm text-slate-500">No AI summary yet.</p>
                              )}
                            </div>
                            <div>
                              <p className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-[#FF6B00]">
                                <MessageCircle className="h-3.5 w-3.5" /> Suggested First Reply
                              </p>
                              {q.ai_suggested_reply ? (
                                <div className="mt-2 flex items-start gap-2">
                                  <p data-testid="admin-ai-reply" className="flex-1 rounded-lg border border-white/10 bg-[#080C14] p-3 text-sm leading-relaxed text-slate-200">
                                    {q.ai_suggested_reply}
                                  </p>
                                  <button
                                    onClick={() => copyReply(q.ai_suggested_reply)}
                                    data-testid="admin-copy-reply"
                                    aria-label="Copy suggested reply"
                                    className="shrink-0 rounded-lg border border-white/15 p-2 text-slate-400 transition-colors hover:border-[#FF6B00] hover:text-[#FF6B00]"
                                  >
                                    <Copy className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => runTriage(q.id)}
                                  disabled={triagingId === q.id}
                                  data-testid="admin-ai-generate"
                                  className="mt-2 flex items-center gap-2 rounded-full border border-[#FF6B00]/50 px-4 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#FF6B00] transition-colors hover:bg-[#FF6B00] hover:text-[#080C14] disabled:opacity-60"
                                >
                                  <Sparkles className={`h-3.5 w-3.5 ${triagingId === q.id ? "animate-pulse" : ""}`} />
                                  {triagingId === q.id ? "Generating..." : "Generate with AI"}
                                </button>
                              )}
                            </div>
                          </div>
                          {q.attachments && q.attachments.length > 0 && (
                            <div className="mt-5 border-t border-white/10 pt-4">
                              <p className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-[#FF6B00]">
                                <FileText className="h-3.5 w-3.5" /> Attachments
                              </p>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {q.attachments.map((a) => (
                                  <a
                                    key={a.id}
                                    href={`${API}/attachments/${a.id}/download`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="admin-attachment-link"
                                    className="flex items-center gap-2 rounded-full border border-white/15 bg-[#080C14] px-3 py-1.5 text-xs text-slate-200 transition-colors hover:border-[#FF6B00] hover:text-[#FF6B00]"
                                  >
                                    <FileText className="h-3 w-3 text-[#FF6B00]" />
                                    {a.original_filename.length > 30 ? `${a.original_filename.slice(0, 27)}...` : a.original_filename}
                                    <span className="text-slate-500">{fmtSize(a.size)}</span>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
